/**
 * CriteriaPanel — left pane of ScoringWorkspace. CRUD criteria with inline
 * editing, drag-to-reorder (desktop) or up/down arrows (mobile), and a
 * weight-summary banner that signals rubric health (=100% / <100% / >100%).
 *
 * Replaces the basic E2 list (which exposed only Add / Edit / Delete) with
 * the full E3 surface: WeightBar per row, drag-reorder via @dnd-kit, debounced
 * inline edits, and an explicit total-weight callout.
 *
 * Linear: MYD-208 (E3). The prop interface (`onAddClick`, `onEditClick`,
 * `onApplyTemplateClick`) stays compatible with E2's ScoringWorkspace.
 */

import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { Card, CardHeader, CardContent } from 'components/ui/card'
import { Button } from 'components/ui/button'
import {
	Plus,
	ListChecks,
	LayoutTemplate,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	Save,
} from 'lucide-react'
import { cn } from 'lib/utils'
import useMediaQuery from 'hooks/useMediaQuery'

import { reorderCriteria } from 'slices/scoringSlice'
import CriterionRow from './CriterionRow'

interface CriteriaPanelProps {
	evalId: string
	onAddClick: () => void
	onEditClick: (criterion: any) => void
	onApplyTemplateClick: () => void
	onSaveAsTemplateClick: () => void
}

const CriteriaPanel = ({
	evalId,
	onAddClick,
	onApplyTemplateClick,
	onSaveAsTemplateClick,
}: CriteriaPanelProps) => {
	const dispatch = useDispatch()
	const isDesktop = useMediaQuery('(min-width: 768px)')

	const criteria = useSelector((state: any) =>
		(state.scoring?.criteriaByEvaluation?.[evalId] || [])
			.slice()
			.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
	)

	const totalWeight = useMemo(
		() => criteria.reduce((sum: number, c: any) => sum + (c.weight || 0), 0),
		[criteria],
	)

	const totalState = useMemo(() => {
		if (totalWeight === 100) {
			return {
				icon: CheckCircle2,
				colour: 'text-emerald-700',
				bg: 'bg-emerald-50 border-emerald-200',
				message: 'Total: 100% — rubric balanced.',
			}
		}
		if (totalWeight < 100) {
			return {
				icon: AlertTriangle,
				colour: 'text-amber-700',
				bg: 'bg-amber-50 border-amber-200',
				message: `Total: ${totalWeight}% — set ${100 - totalWeight}% more to reach 100%.`,
			}
		}
		return {
			icon: XCircle,
			colour: 'text-red-700',
			bg: 'bg-red-50 border-red-200',
			message: `Total: ${totalWeight}% — over by ${totalWeight - 100}%. Reduce weights to reach 100%.`,
		}
	}, [totalWeight])

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			const { active, over } = event
			if (!over || active.id === over.id) return
			const oldIndex = criteria.findIndex((c: any) => c._id === active.id)
			const newIndex = criteria.findIndex((c: any) => c._id === over.id)
			if (oldIndex === -1 || newIndex === -1) return
			const reordered = arrayMove(criteria, oldIndex, newIndex)
			// @ts-ignore — thunk
			dispatch(reorderCriteria({ evalId, orderedIds: reordered.map((c: any) => c._id) }))
		},
		[criteria, dispatch, evalId],
	)

	const moveBy = useCallback(
		(index: number, delta: number) => {
			const target = index + delta
			if (target < 0 || target >= criteria.length) return
			const reordered = [...criteria]
			const [item] = reordered.splice(index, 1)
			reordered.splice(target, 0, item)
			// @ts-ignore — thunk
			dispatch(reorderCriteria({ evalId, orderedIds: reordered.map((c: any) => c._id) }))
		},
		[criteria, dispatch, evalId],
	)

	const TotalIcon = totalState.icon

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between gap-2">
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<ListChecks className="w-4 h-4" />
						Criteria ({criteria.length})
					</h4>
				</div>
				<div className="flex items-center gap-2 mt-2">
					<Button variant="primary" onClick={onAddClick} className="flex-1 gap-1.5">
						<Plus className="w-4 h-4" />
						Add
					</Button>
					<Button
						variant="primary"
						isOutline
						onClick={onApplyTemplateClick}
						className="gap-1.5"
						title="Apply scoring template"
						aria-label="Apply scoring template"
					>
						<LayoutTemplate className="w-4 h-4" />
					</Button>
					<Button
						variant="primary"
						isOutline
						onClick={onSaveAsTemplateClick}
						disabled={criteria.length === 0}
						className="gap-1.5"
						title="Save current criteria as a new template"
						aria-label="Save current criteria as a new template"
					>
						<Save className="w-4 h-4" />
					</Button>
				</div>
			</CardHeader>
			<CardContent>
				{criteria.length === 0 ? (
					<div className="text-center py-6 text-sm text-gray-400">
						No criteria yet. Add one to begin scoring.
					</div>
				) : (
					<>
						{/* Total-weight banner */}
						<div
							className={cn(
								'flex items-start gap-2 px-3 py-2 rounded-md border text-sm mb-3',
								totalState.bg,
								totalState.colour,
							)}
						>
							<TotalIcon className="w-4 h-4 mt-0.5 shrink-0" />
							<span>{totalState.message}</span>
						</div>

						{/* Sortable list */}
						<DndContext
							sensors={sensors}
							collisionDetection={closestCenter}
							onDragEnd={handleDragEnd}
						>
							<SortableContext
								items={criteria.map((c: any) => c._id)}
								strategy={verticalListSortingStrategy}
							>
								<div className="space-y-2">
									{criteria.map((c: any, idx: number) => (
										<CriterionRow
											key={c._id}
											criterion={c}
											evalId={evalId}
											totalWeight={totalWeight}
											index={idx}
											count={criteria.length}
											disableDrag={!isDesktop}
											onMoveUp={() => moveBy(idx, -1)}
											onMoveDown={() => moveBy(idx, +1)}
										/>
									))}
								</div>
							</SortableContext>
						</DndContext>
					</>
				)}
			</CardContent>
		</Card>
	)
}

export default CriteriaPanel
